import { Button, TabsWithContent, useModal, useQueryState } from "@homebound/beam";
import { useMemo } from "react";
import { Loading } from "src/components";
import {
  BidContractPlanPricing_RevisionFragment,
  useBidContractPlanPricing_ReadyPlanOptionsQuery,
  useBidContractPlanPricing_RevisionQuery,
  useBidContractPlanPricing_ScopeLinesQuery,
} from "src/components/bid-contracts/BidContractPlanPricingPage.generated";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ImportBidModal } from "src/routes/bids/components/ImportBidModal";
import { ProrateBidView } from "src/routes/bids/components/ProrateBidModal";
import { fail, queryResult } from "src/utils";

export function BidContractPlanPricingPage() {
  const { bidContractRevisionId } = useRouteParams<{ bidContractRevisionId: string }>();
  const query = useBidContractPlanPricing_RevisionQuery({ variables: { bidContractRevisionId } });
  return queryResult(query, ({ bidContractRevision }) => (
    <BidContractPlanPricingView bidContractRevision={bidContractRevision} />
  ));
}

type BidContractPlanPricingViewProps = { bidContractRevision: BidContractPlanPricing_RevisionFragment };

function BidContractPlanPricingView(props: BidContractPlanPricingViewProps) {
  const { bidContractRevision } = props;
  const { openModal } = useModal();
  const bidPackageVersion = bidContractRevision.bidPackageVersion!;
  const sortedRpavs = useMemo(
    () => bidPackageVersion.readyPlanAggregateVersions.sortBy((rpav) => rpav.readyPlan.id),
    [bidPackageVersion.readyPlanAggregateVersions],
  );
  const [selectedTab, setSelectedTab] = useQueryState<string>("planId", sortedRpavs[0].readyPlan.id);
  const selectedReadyPlan = useMemo(() => {
    return sortedRpavs.find((rpav) => rpav.readyPlan.id === selectedTab)?.readyPlan ?? fail("ReadyPlan not found");
  }, [selectedTab, sortedRpavs]);
  const query = useBidContractPlanPricing_ScopeLinesQuery({
    variables: { bidPackageVersionId: bidPackageVersion.id, filter: { readyPlan: [selectedTab] } },
  });
  const readyPlanOptionsQuery = useBidContractPlanPricing_ReadyPlanOptionsQuery({
    variables: { readyPlanId: selectedTab },
  });
  const readyPlanOptionsById = useMemo(
    () => readyPlanOptionsQuery.data?.readyPlan.options.keyBy((option) => option.id) ?? {},
    [readyPlanOptionsQuery.data?.readyPlan.options],
  );

  return (
    <TabsWithContent
      right={
        bidContractRevision.bidContract.isInternalEstimate ? undefined : (
          <Button
            label="Upload Costs"
            variant="text"
            size="sm"
            disabled={!bidContractRevision.canEdit.allowed}
            onClick={() =>
              openModal({
                content: (
                  <ImportBidModal
                    requests={[bidContractRevision.bidContract.bidRequest!]}
                    bidPackageName={bidContractRevision.bidContract.name}
                    latestBidContractRevisions={[bidContractRevision]}
                    bidPackageVersionId={bidPackageVersion.id}
                    isUnitBased={false}
                    tradePartnerId={bidContractRevision.bidContract.tradePartner?.id}
                  />
                ),
              })
            }
          />
        )
      }
      alwaysShowAllTabs
      tabs={sortedRpavs.map((rpav) => ({
        name: rpav.displayName,
        value: rpav.readyPlan.id,
        render: () => {
          if (query.loading || readyPlanOptionsQuery.loading) return <Loading />;
          return queryResult(query, ({ bidPackageVersionScopeLines }) => (
            <ProrateBidView
              bidContractRevisionId={bidContractRevision.id}
              readyPlan={selectedReadyPlan}
              readyPlanOptionsById={readyPlanOptionsById}
              scopeLineItems={bidPackageVersionScopeLines.map((sli) => sli.takeoffLineItemVersion)}
              type={bidContractRevision.bidContract.type!.code}
              existingTradeLineItems={bidContractRevision.tradeLineItems}
              modalTreatment={false}
              disabled={!bidContractRevision.canEdit.allowed}
            />
          ));
        },
      }))}
      selected={selectedTab}
      onChange={setSelectedTab}
    />
  );
}
